import React, { useContext, useState } from "react";
import { useInView } from "react-intersection-observer";
import PropTypes from "prop-types";
import { range } from "lodash";

import { Button } from "semantic-ui-react";

import { DraggableContainer } from "components/DragDrop";
import InfiniteScroll from "components/InfiniteScroll";

import {
  useAccessTypeStore,
  useDuplicateWorkflowAction,
  useWorkflow,
  useWorkflowEditor,
} from "./hooks";
import { HubContext, ToasterContext } from "../../Provider";
import { AddClientDropdown } from "..";
import {
  ArchiveClientModal,
  ClientTile,
  ClientTileEditor,
  DeleteClientModal,
  FormModal,
  HiddenContainer,
  TextualTile,
  TilePlaceholder,
  WorkflowColumn,
  WorkflowEditor,
  WorkflowMenu,
} from "./components";
import {
  assignClientWorkflow,
  assignTask,
  markNextTaskAsCompleted,
  moveClientTileToTop,
} from "./helper";

export function WorkflowContainer({ workflow, filters, onHide, onDuplicated }) {
  const { ref: visibilityTracker, inView } = useInView({ triggerOnce: true });

  const { viewClientCard, submitSortWorkflows } = useContext(HubContext);
  const { fireSuccess, fireError } = useContext(ToasterContext);

  const { page, loading, empty, clientWorkflows, loadData, notify } =
    useWorkflow(workflow.id, filters, inView);

  const [clientWfToDelete, openDeleteModal] = useState(false);
  const [clientWfToArchive, openArchiveModal] = useState(false);
  const [isClientTileEditorOpen, openClientTileEditor] = useState(false);
  const [taskForm, setTaskForm] = useState(null);

  const [editMode, setEditMode] = useState(Boolean(workflow.editMode));

  const {
    loadingWorkflowForEditor,
    workflowWithEditorData,
    loadWorkflowDataForEditor,
  } = useWorkflowEditor(workflow.id, inView);

  const duplicateWorkflow = useDuplicateWorkflowAction(onDuplicated);

  const access = useAccessTypeStore();

  if (inView && !loading && empty && filters.filterEmptyWorkflows) {
    return <HiddenContainer />;
  }

  if (editMode) {
    return (
      <WorkflowEditor
        loading={loadingWorkflowForEditor || !workflowWithEditorData}
        workflow={workflowWithEditorData}
        reloadData={loadWorkflowDataForEditor}
        onDeleted={onHide}
        onClose={() => {
          setEditMode(false);
        }}
      />
    );
  }

  const workflowName = () => {
    return workflowWithEditorData?.name || workflow.name;
  };

  const tileOption = () => {
    return (
      workflowWithEditorData?.clientTileOptions || workflow.clientTileOptions
    );
  };

  const handleOnTopTaskComplete = (task) => {
    return markNextTaskAsCompleted(task)
      .then(() => {
        notify();
        fireSuccess(`Successfully completed task ${task.title}`);
      })
      .catch((error) => {
        fireError(
          error?.response?.data
            ? Object.values(error?.response?.data)[0]
            : "Failed to mark Client's Task as completed"
        );
      });
  };

  return (
    <div ref={visibilityTracker}>
      <WorkflowColumn
        viewOnly={!access.hasAccess()}
        id={workflow.id}
        testId={workflowName()}
        title={
          <WorkflowColumn.Loader active={loading || loadingWorkflowForEditor}>
            <WorkflowColumn.Header>{workflowName()}</WorkflowColumn.Header>
          </WorkflowColumn.Loader>
        }
        description={workflow.process?.name}
        color={workflow.process?.color}
        showNewTempInfo={workflow.isNewTemplate}
        showRuleInfo={
          workflow?.hasWorkflowRules || workflowWithEditorData?.hasWorkflowRules
        }
        onDoubleClickTitle={() => {
          setEditMode(true);
        }}
        style={{ minHeight: "3em" }}
        menuSlot={
          <WorkflowMenu
            viewOnly={!access.hasAccess()}
            onClickEditWorkflow={() => {
              setEditMode(true);
            }}
            onClickSort={(type) => {
              return submitSortWorkflows(workflow.id, type)
                .then(() => {
                  loadData();
                  fireSuccess(`Workflow ${workflow.name} has been sorted`);
                })
                .catch(() => {
                  loadData();
                  fireError(`Failed to sort Workflow ${workflow.name}`);
                });
            }}
            onClickHideWorkflow={onHide}
            onClickDuplicateWorkflow={() => {
              duplicateWorkflow(workflow.id, workflow.name);
            }}
            onClickModifyTitle={() => {
              openClientTileEditor(true);
            }}
          />
        }
        topChildren={
          <AddClientDropdown
            workflowId={workflow.id}
            onClientsAdded={loadData}
            style={{ paddingBottom: 0 }}
            data-test={`workflow-add-clients-${workflow.name}`}
          />
        }
      >
        {loading &&
          clientWorkflows.length === 0 &&
          (workflow.numActiveClientWorkflows === 0 ? (
            <TextualTile text="Drag a client tile here" />
          ) : (
            range(workflow.numActiveClientWorkflows).map((idx) => {
              return <TilePlaceholder key={idx} />;
            })
          ))}

        {!loading && clientWorkflows.length === 0 && (
          <TextualTile text="Drag a client tile here" />
        )}

        <InfiniteScroll
          loadingPage={page.loading}
          hasMorePages={page.hasMorePages}
          onLoadMore={page.readNext}
          loadingElement={<TilePlaceholder />}
          pullNextPageElement={
            <div style={{ textAlign: "center" }}>
              <Button
                size="tiny"
                basic
                onClick={page.readNext}
                style={{
                  textAlign: "center",
                  alignSelf: "center",
                  marginBottom: "1em",
                  color: "grey",
                }}
              >
                Load more
              </Button>
            </div>
          }
        >
          {clientWorkflows.map((clientWf, i) => {
            const draggableId = `${clientWf.client.id}:${clientWf.id}`;
            return (
              <DraggableContainer index={i} key={clientWf.id} id={draggableId}>
                <ClientTile
                  clientTileAppearance={tileOption()}
                  data={clientWf}
                  onClick={() => {
                    const { id, client } = clientWf;
                    viewClientCard(client.id, id);
                  }}
                  onDelete={openDeleteModal}
                  onArchive={openArchiveModal}
                  onAssign={async ({ assignee, cwId, taskId }) => {
                    const assign = taskId
                      ? (a) => {
                          return assignTask(taskId, a);
                        }
                      : (a) => {
                          return assignClientWorkflow(cwId, a);
                        };

                    try {
                      await assign(assignee);
                      loadData();
                      fireSuccess("Assigned Client Workflow Successfully");
                    } catch (e) {
                      fireError("Failed to assign Client Workflow");
                    }
                  }}
                  onTopTaskComplete={(task) => {
                    const complete =
                      task?.formInstanceId && !task?.formInstance?.isFilled
                        ? setTaskForm
                        : handleOnTopTaskComplete;
                    complete(task);
                  }}
                  onMoveUp={() => {
                    moveClientTileToTop(clientWf.id, clientWorkflows[0].id)
                      .then(() => {
                        loadData();
                        fireSuccess("Client has moved to the top");
                      })
                      .catch(() => {
                        fireError("Failed to moved client to the top");
                      });
                  }}
                />
              </DraggableContainer>
            );
          })}
        </InfiniteScroll>
      </WorkflowColumn>

      {clientWfToDelete && (
        <DeleteClientModal
          clientWfId={clientWfToDelete}
          onComplete={loadData}
          onClose={() => {
            openDeleteModal(false);
          }}
        />
      )}

      {clientWfToArchive && (
        <ArchiveClientModal
          clientWfId={clientWfToArchive}
          onComplete={loadData}
          onClose={() => {
            openArchiveModal(false);
          }}
        />
      )}

      {isClientTileEditorOpen && (
        <ClientTileEditor
          id={workflow.id}
          name={workflowName()}
          clientTileOptions={tileOption()}
          onClose={({ numberOfTasks }) => {
            if (numberOfTasks !== tileOption().numberOfTasks) loadData();
            loadWorkflowDataForEditor();
            openClientTileEditor(false);
          }}
        />
      )}
      {taskForm && (
        <FormModal
          formId={taskForm.formInstanceId}
          onClose={() => {
            setTaskForm(null);
          }}
          onDone={() => {
            handleOnTopTaskComplete(taskForm).finally(() => {
              setTaskForm(null);
            });
          }}
        />
      )}
    </div>
  );
}

WorkflowContainer.propTypes = {
  workflow: PropTypes.object.isRequired,
  filters: PropTypes.object.isRequired,
  onHide: PropTypes.func.isRequired,
  onDuplicated: PropTypes.func.isRequired,
};

WorkflowContainer.defaultProps = {};

export default WorkflowContainer;
